import { useCallback, useEffect, useMemo, useState } from "react";

interface UseColumnOrderOptions {
  storageKey: string;
  columnKeys: string[];
}

/**
 * Persists a user-defined column order in localStorage. Reconciles the saved
 * order with the live column list each render so newly added columns appear at
 * the end and removed columns are dropped automatically.
 */
export function useColumnOrder({ storageKey, columnKeys }: UseColumnOrderOptions) {
  // Guard: if caller passes something non-iterable (e.g. undefined during a
  // concurrent render or stale closure), fall back to an empty array so we
  // never crash with "columnKeys is not iterable".
  const safeKeys: string[] = Array.isArray(columnKeys) ? columnKeys : [];

  const [savedOrder, setSavedOrder] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : [];
    } catch {
      return [];
    }
  });

  const orderedKeys = useMemo(() => {
    const liveSet = new Set(safeKeys);
    const seen = new Set<string>();
    const result: string[] = [];
    for (const k of savedOrder) {
      if (liveSet.has(k) && !seen.has(k)) {
        result.push(k);
        seen.add(k);
      }
    }
    for (const k of safeKeys) {
      if (!seen.has(k)) {
        result.push(k);
        seen.add(k);
      }
    }
    return result;
  }, [savedOrder, safeKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(orderedKeys));
    } catch {
      // ignore quota errors
    }
  }, [orderedKeys, storageKey]);

  const moveColumn = useCallback(
    (sourceKey: string, targetKey: string) => {
      if (sourceKey === targetKey) return;
      setSavedOrder((prev) => {
        const base = prev.length ? prev.filter((k) => safeKeys.includes(k)) : [...safeKeys];
        // Make sure both keys exist in the working array.
        if (!base.includes(sourceKey)) base.push(sourceKey);
        if (!base.includes(targetKey)) base.push(targetKey);
        const without = base.filter((k) => k !== sourceKey);
        const targetIdx = without.indexOf(targetKey);
        without.splice(targetIdx, 0, sourceKey);
        // Append any keys not yet placed (newly added columns)
        for (const k of safeKeys) {
          if (!without.includes(k)) without.push(k);
        }
        return without;
      });
    },
    [safeKeys]
  );

  const resetOrder = useCallback(() => setSavedOrder([]), []);

  return { orderedKeys, moveColumn, resetOrder };
}
